/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/ProposalsListModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import React, { useState, useEffect } from "react";
import styles from "./ProposalsListModal.module.css";
import { useNavigate } from "react-router";
import RejectProposalModal from "../RejectProposalModal/index.jsx";
import ProposalDisplayModal from "../ProposalDisplayModal/index.jsx";
import Proposal from "./Proposal.jsx";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Switch from "@mui/material/Switch";
import { Backdrop, CircularProgress } from "@mui/material";
import { ReactComponent as CalendarIcon } from "../../assets/icons/ValidationPage/CalendarIcon.svg";

import {
  getProposals,
  sortProposalsByDestacado,
  filterProposals,
  getCategories,
  handleProposalAction,
  handleRejectSubmit,
  handleToggleActive,
  capitalizeFirstLetter,
  getCategoryTitle,
} from "../../logic/proposal/index.js";
import { ReactComponent as ActiveRequirementIcon } from "../../assets/icons/ProposalsListModal/ActiveRequirementIcon.svg";
import { ReactComponent as RequirementCategoryIcon } from "../../assets/icons/ProposalsListModal/RequirementCategoryIcon.svg";

import { useTranslation } from "react-i18next";
import AssetImage from "../../components/AssetImage";

const ProposalsListModal = ({ requirement, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [proposals, setProposals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDisplayModal, setShowDisplayModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);
  const [acceptedExpanded, setAcceptedExpanded] = useState(true);
  const [rejectedExpanded, setRejectedExpanded] = useState(false);
  const [isActive, setIsActive] = useState(requirement?.activo || false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!!token && token.trim() !== "undefined" && requirement) {
      const loadInitialData = async () => {
        setIsLoading(true);
        try {
          await Promise.all([fetchData(), fetchCategories()]);
        } catch (error) {
          logger.error("Error loading data:", { err: sanitizeError(error) });
        } finally {
          setIsLoading(false);
        }
      };
      loadInitialData();
    } else {
      setIsLoading(false);
      if (!requirement) {
        logger.info(
          "Error trying to get the token to retrieve the users data.",
        );
      }
    }
  }, [requirement]);

  const fetchCategories = async () => {
    const categoriesData = await getCategories(navigate);
    setCategories(categoriesData);
  };

  const fetchData = async () => {
    try {
      const proposalsData = await getProposals(navigate);
      const filteredProposals = (proposalsData || []).filter(
        (proposal) => proposal.idRequerimiento === requirement._id,
      );
      setProposals(filteredProposals);
    } catch (error) {
      logger.error("Error fetching data:", { err: sanitizeError(error) });
    }
  };

  const onProposalAction = async (proposalId, action) => {
    if (action === "star" || action === "unstar") {
      const updatedProposals = proposals.map((proposal) =>
        proposal._id === proposalId
          ? { ...proposal, esDestacado: action === "star" }
          : proposal,
      );

      setProposals(updatedProposals);
    }

    await handleProposalAction(
      proposalId,
      action,
      proposals,
      navigate,
      setSelectedProposal,
      setShowRejectModal,
      fetchData,
    );
  };

  const onRejectSubmit = async () => {
    await handleRejectSubmit(fetchData, setShowSuccessMessage, setIsSlidingUp);
  };

  const onToggleActive = async () => {
    const newActiveState = await handleToggleActive(
      requirement._id,
      isActive,
      navigate,
    );
    setIsActive(newActiveState);
  };

  const handleViewProposal = (proposal) => {
    setSelectedProposal(proposal);
    setShowDisplayModal(true);
  };

  const renderProposalsList = (filteredProposals, sectionType) => {
    if (!filteredProposals || filteredProposals.length === 0) {
      return (
        <div className={styles.emptyMessage}>
          {sectionType === "pending" &&
            t("components.proposalsListModal.noPendingProposals")}
          {sectionType === "accepted" &&
            t("components.proposalsListModal.noAcceptedProposals")}
          {sectionType === "rejected" &&
            t("components.proposalsListModal.noRejectedProposals")}
        </div>
      );
    }

    const sortedProposals = sortProposalsByDestacado(filteredProposals);

    return sortedProposals.map((proposal, index) => {
      const user = proposal.usuario ?? null;
      const company = proposal.usuario?.empresa ?? null;
      return (
        <div key={proposal._id || index}>
          <Proposal
            proposal={proposal}
            user={user}
            company={company}
            onProposalAction={onProposalAction}
            onViewProposal={handleViewProposal}
            sectionType={sectionType}
          />
        </div>
      );
    });
  };

  const handleModalClose = async (modalType) => {
    if (modalType === "reject") {
      setShowRejectModal(false);
    } else if (modalType === "display") {
      setShowDisplayModal(false);
    } else {
      logger.warn("Unknown modal type on close", { data: modalType });
    }
    setSelectedProposal(null);
    try {
      await fetchData();
    } catch (error) {
      logger.error("Error fetching data after modal close:", {
        err: sanitizeError(error),
      });
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalLeftSection}>
          <div className={styles.requirementTitle}>{requirement?.titulo}</div>

          <div className={styles.requirementStatus}>
            <ActiveRequirementIcon style={{ fill: "#00E864" }} />
            <span className={styles.requirementStatusText}>
              {isActive
                ? t("components.proposalsListModal.activeRequirement")
                : t("components.proposalsListModal.inactiveRequirement")}
            </span>
          </div>

          <div className={styles.categoryContainer}>
            <div className={styles.categoryBox}>
              <RequirementCategoryIcon />
              <span className={styles.categoryText}>
                {t("components.proposalsListModal.category")}:{" "}
                {getCategoryTitle(categories, requirement?.idCategoria)}
              </span>
            </div>
            <div className={styles.categoryBox}>
              <RequirementCategoryIcon />
              <span className={styles.categoryText}>
                {t("components.proposalsListModal.subcategory")}:{" "}
                {requirement?.nombreSubcategoria}
              </span>
            </div>
          </div>

          <div>
            <div className={styles.requirementLabel}>
              {t("components.proposalsListModal.description")}:
            </div>
            <div className={styles.requirementValue}>
              {requirement?.descripcion}
            </div>
          </div>

          <div>
            <div className={styles.requirementLabel}>
              {t("components.proposalsListModal.location")}:
            </div>
            <div className={styles.requirementValue}>
              {requirement?.ubicacion}
            </div>
          </div>

          <div>
            <div className={styles.requirementLabel}>
              {t("components.proposalsListModal.limitDate")}:
            </div>
            <div className={styles.requirementValue}>
              <CalendarIcon className={styles.calendarIcon} />
              {capitalizeFirstLetter(
                new Date(requirement?.fechaLimite).toLocaleDateString("es-ES", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }),
              )}
            </div>
          </div>

          <div>
            <div className={styles.requirementLabel}>
              {t("components.proposalsListModal.minimumExperience")}:
            </div>
            <div className={styles.requirementRowText}>
              {requirement.experienciaMinima
                ? requirement.experienciaMinima +
                  " " +
                  t("components.requirementDisplayModal.years")
                : t("common.notAvailable")}
            </div>
          </div>

          <div className={styles.requirementsGrid}>
            {requirement?.certificaciones?.length > 0 && (
              <>
                <div>
                  <div className={styles.requirementLabel}>
                    {t("components.proposalsListModal.certifications")}:
                  </div>
                  {requirement.certificaciones.map((certification, index) => (
                    <div key={index} className={styles.requirementRow}>
                      <div className={styles.requirementRowText}>
                        {certification.nombre}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          {requirement?.urlImagen && (
            <div className={styles.imageContainer}>
              <AssetImage
                className={styles.image}
                src={requirement.urlImagen}
                alt={t("components.proposalsListModal.requirementImage")}
              />
            </div>
          )}
          <div className={styles.toggleContainer}>
            <span className={styles.toggleText}>
              {t("components.proposalsListModal.activateDeactivateRequirement")}
            </span>
            <Switch
              checked={isActive}
              onChange={onToggleActive}
              color="primary"
            />
          </div>
        </div>

        <div className={styles.modalRightSection}>
          <div className={styles.modalHeader}>
            <div className={styles.mainTitle}>
              {t("components.proposalsListModal.proposals")}
            </div>
            <button className={styles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>
          <div className={styles.proposalsList}>
            {renderProposalsList(filterProposals.pending(proposals), "pending")}
          </div>

          <div className={styles.proposalsListContainer}>
            <div
              className={styles.title}
              onClick={() => setAcceptedExpanded(!acceptedExpanded)}
            >
              {t("components.proposalsListModal.accepted")}
              <KeyboardArrowDownIcon
                className={`${styles.arrowIcon} ${
                  acceptedExpanded ? "" : styles.rotated
                }`}
              />
            </div>
            <div
              className={`${styles.divider} ${
                !acceptedExpanded ? styles.hidden : ""
              }`}
            ></div>
            <div
              className={`${styles.proposalsList} ${styles.collapsible} ${
                acceptedExpanded ? styles.expanded : ""
              }`}
            >
              {renderProposalsList(
                filterProposals.accepted(proposals),
                "accepted",
              )}
            </div>
          </div>

          <div className={styles.proposalsListContainer}>
            <div
              className={styles.title}
              onClick={() => setRejectedExpanded(!rejectedExpanded)}
            >
              {t("components.proposalsListModal.rejected")}
              <KeyboardArrowDownIcon
                className={`${styles.arrowIcon} ${
                  rejectedExpanded ? "" : styles.rotated
                }`}
              />
            </div>
            <div
              className={`${styles.divider} ${
                !rejectedExpanded ? styles.hidden : ""
              }`}
            ></div>
            <div
              className={`${styles.proposalsList} ${styles.collapsible} ${
                rejectedExpanded ? styles.expanded : ""
              }`}
            >
              {renderProposalsList(
                filterProposals.rejected(proposals),
                "rejected",
              )}
            </div>
          </div>

          {showRejectModal && selectedProposal && (
            <RejectProposalModal
              open={showRejectModal}
              proposal={selectedProposal}
              onClose={() => handleModalClose("reject")}
              onSend={onRejectSubmit}
            />
          )}

          {showDisplayModal && selectedProposal && (
            <ProposalDisplayModal
              open={showDisplayModal}
              proposal={selectedProposal}
              requirement={requirement}
              onClose={() => handleModalClose("display")}
            />
          )}

          {showSuccessMessage && (
            <div
              className={`${styles.successMessage} ${
                isSlidingUp ? styles.slideUp : ""
              }`}
            >
              <CheckCircleIcon className={styles.successMessageIcon} />
              {t("components.proposalsListModal.successMessageText1")} <br />
              {t("components.proposalsListModal.successMessageText2")}
            </div>
          )}
        </div>
      </div>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default ProposalsListModal;
